import React, { useContext, useState, useCallback } from "react";
import { WorkoutContext } from "../../contexts/WorkoutContext";
import { useAuth } from "../../contexts/AuthContext";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "../ui/dialog";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Switch } from "../ui/switch";
import { Save, Check, Loader2 } from "lucide-react";
import { createSession } from "../../services/api";
import useTotalDuration from "../../hooks/useTotalDuration";

const SaveSessionDialog = ({ open, onOpenChange }) => {
  const { workoutPlan } = useContext(WorkoutContext);
  const { isAuthenticated } = useAuth();
  const totalSeconds = useTotalDuration(workoutPlan);

  const [name, setName] = useState("");
  const [isFavourite, setIsFavourite] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  // Reset state when dialog opens
  React.useEffect(() => {
    if (open) {
      setName(workoutPlan?.label || "");
      setIsFavourite(false);
      setSaving(false);
      setSaved(false);
    }
  }, [open, workoutPlan]);

  const handleSave = useCallback(async () => {
    if (!workoutPlan) return;
    setSaving(true);

    const durationMinutes = Math.round(totalSeconds / 60) || 1;

    const sessionData = {
      started_at: new Date().toISOString(),
      ended_at: new Date().toISOString(),
      duration_minutes: durationMinutes,
      workout_type: name || workoutPlan.label || "Workout",
      plan_data: { ...workoutPlan, label: name || workoutPlan.label },
      is_favourite: isFavourite,
    };

    try {
      if (isAuthenticated) {
        await createSession(sessionData);
      } else {
        const stored = JSON.parse(localStorage.getItem("completedSessions") || "[]");
        stored.unshift({
          ...sessionData,
          id: `local-${Date.now()}`,
          created_at: new Date().toISOString(),
        });
        if (stored.length > 100) stored.length = 100;
        localStorage.setItem("completedSessions", JSON.stringify(stored));
      }
      setSaved(true);
      setTimeout(() => onOpenChange(false), 1200);
    } catch (err) {
      console.error("Failed to save session:", err);
      setSaving(false);
    }
  }, [workoutPlan, name, isFavourite, isAuthenticated, onOpenChange]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Save session</DialogTitle>
          <DialogDescription>
            Save this session for later use.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          <div className="space-y-2">
            <Label htmlFor="session-name">Session name</Label>
            <Input
              id="session-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g., Morning HIIT Blast"
            />
          </div>
          <div className="flex items-center space-x-3">
            <Switch
              id="save-favourite"
              checked={isFavourite}
              onCheckedChange={setIsFavourite}
            />
            <Label htmlFor="save-favourite">Mark as favourite</Label>
          </div>
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={saving}
          >
            Cancel
          </Button>
          <Button onClick={handleSave} disabled={saving || saved}>
            {saved ? (
              <>
                <Check className="mr-2 h-4 w-4 text-green-500" />
                Saved!
              </>
            ) : saving ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Saving...
              </>
            ) : (
              <>
                <Save className="mr-2 h-4 w-4" />
                Save session
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default SaveSessionDialog;
